import * as React from 'react';
import {noop} from 'lodash';

import {emptyObject} from '@packages/utilities/fp/utils';

import type {SelectOption, SelectProps} from './_types';
import {
  SelectTriggerButtonHStack,
  SelectTriggerButtonLabel,
  SelectTriggerButtonView,
} from './Select.styles';

import type {ButtonProps} from '../Button';
import {HStack, VStack} from '../Stack';
import {Text, type TextProps} from '../Text';
import {renderElement} from '../../utils';
import {ChevronSmallDownIcon} from '../../icons';
import {useMergeRefs} from '@floating-ui/react';

type SelectTriggerProps = {
  /**
   * The content of the SelectTrigger.
   */
  children: React.ReactNode;
  /**
   * Whether the SelectTrigger is currently rendering a placeholder.
   */
  _isPlaceholder?: boolean;
  /**
   * The prefix of the SelectTrigger.
   */
  prefix?: SelectOption['icon'];
  /**
   * Whether to truncate the button text.
   */
  shouldTruncateButtonText?: boolean;
  /**
   * Internal prop for controlling the amount of left padding on the trigger button label text. Should only be used in Designer in the medium size context. Adds more padding to match our other legacy Selects in Designer when `true`. Defaults to `false`.
   */
  _shouldUseLegacyAlignment?: boolean;
  /**
   * The suffix of the SelectTrigger.
   */
  suffix?: SelectOption['suffix'];
  /**
   * Controls the trigger chevron:
   * - `true` — always hidden
   * - `false` — always shown (ghost selects still hide it when disabled)
   * - omitted — ghost hides at rest, reveals on hover/focus/open; other variants always show
   */
  shouldHideChevron?: boolean;
  /**
   * Whether the select menu is currently open.
   */
  isOpen?: boolean;
  /**
   * The color variant of the text within SelectTrigger. Used internally to set the placeholder text color.
   */
  _textColorVariant?: TextProps['colorVariant'];
} & Omit<ButtonProps, 'prefix' | 'suffix' | 'truncationProps'> &
  Pick<
    SelectProps,
    | 'colorVariant'
    | 'id'
    | 'isBranded'
    | 'isDisabled'
    | 'isError'
    | 'shouldPositionInline'
    | 'shouldHideChevron'
    | 'variant'
  >;

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(
  (
    {
      children,
      colorVariant: colorVariantProp,
      id,
      isBranded,
      isDisabled,
      isError,
      isOpen,
      _isPlaceholder,
      onKeyDown,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      prefix,
      shouldPositionInline,
      shouldTruncateButtonText,
      _shouldUseLegacyAlignment = false,
      suffix,
      _textColorVariant,
      variant = 'solid',
      shouldHideChevron,
      ...props
    },
    ref
  ) => {
    const [anchorRef, setAnchorRef] = React.useState<HTMLButtonElement | null>(
      null
    );
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    /**
     * The solid variant only supports the default color variant.
     * This prevents users that aren't using TypeScript from setting ColorVariant to anything other than default.
     */
    const colorVariant =
      variant === 'solid' || variant === undefined
        ? 'default'
        : colorVariantProp;

    const truncateToLines = shouldTruncateButtonText ? 1 : undefined;

    const mergedRefs = useMergeRefs([ref, setAnchorRef]);

    /**
     * The chevron should be hidden when:
     * - The `shouldHideChevron` prop is `true`
     * - The `SelectTrigger` has `variant="ghost"` and is not hovered, focused,
     *   or open, unless `shouldHideChevron` is explicitly false.
     * - The `SelectTrigger` has `variant="ghost"` and is disabled.
     */
    const hideChevron =
      shouldHideChevron === true ||
      (variant === 'ghost' &&
        (isDisabled ||
          (shouldHideChevron !== false &&
            !isHovered &&
            !isFocused &&
            !isOpen)));

    const truncationTooltipProps = React.useMemo(
      () =>
        shouldTruncateButtonText
          ? {
              anchorRef,
              isOpen: showTooltip && !_isPlaceholder,
              onOpenChange: noop,
              label: (
                <VStack spacing={0}>
                  {children && typeof children === 'string' ? (
                    <Text colorVariant="primary">{children}</Text>
                  ) : null}
                  {suffix && typeof suffix === 'string' ? (
                    <Text colorVariant="secondary">{suffix}</Text>
                  ) : null}
                </VStack>
              ),
            }
          : emptyObject,
      [
        anchorRef,
        children,
        _isPlaceholder,
        showTooltip,
        shouldTruncateButtonText,
        suffix,
      ]
    );

    const handleOnKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
          setShowTooltip(false);
        }
        onKeyDown?.(event);
      },
      [onKeyDown]
    );

    const handleOnFocus = React.useCallback(
      (event: React.FocusEvent<HTMLElement>) => {
        setShowTooltip(true);
        setIsFocused(true);
        onFocus?.(event);
      },
      [onFocus]
    );

    const handleOnBlur = React.useCallback(
      (event: React.FocusEvent<HTMLElement>) => {
        setShowTooltip(false);
        setIsFocused(false);
        onBlur?.(event);
      },
      [onBlur]
    );

    const handleOnMouseEnter = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setShowTooltip(true);
        setIsHovered(true);
        onMouseEnter?.(event);
      },
      [onMouseEnter]
    );

    const handleOnMouseLeave = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setShowTooltip(false);
        setIsHovered(false);
        onMouseLeave?.(event);
      },
      [onMouseLeave]
    );

    return (
      <SelectTriggerButtonView
        {...props}
        _shouldUseLegacyAlignment={_shouldUseLegacyAlignment}
        colorVariant={colorVariant}
        density="compact"
        id={id}
        isBranded={isBranded}
        isDisabled={isDisabled}
        isError={isError}
        justify="start"
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onKeyDown={handleOnKeyDown}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        ref={mergedRefs}
        shouldEdgeAlignIcons
        shouldPositionInline={shouldPositionInline}
        suffix={hideChevron ? undefined : ChevronSmallDownIcon}
        variant={variant}
      >
        <SelectTriggerButtonLabel truncateToLines={truncateToLines}>
          <HStack justify="space-between" width="100%">
            <SelectTriggerButtonHStack justify="start">
              {prefix && renderElement(prefix)}
              <Text
                colorVariant={_textColorVariant}
                /* We need to assign a key to this text, so that its intersection observer will run when the text changes. */
                key={typeof children === 'string' ? children : undefined}
                truncateToLines={truncateToLines}
                truncationTooltipProps={truncationTooltipProps}
              >
                {children}
              </Text>
            </SelectTriggerButtonHStack>
            {suffix && (
              <>
                {typeof suffix === 'string' ? (
                  <Text
                    colorVariant="secondary"
                    key={suffix}
                    truncateToLines={truncateToLines}
                    truncationTooltipProps={truncationTooltipProps}
                  >
                    {suffix}
                  </Text>
                ) : (
                  renderElement(suffix, {color: '$textSecondary'})
                )}
              </>
            )}
          </HStack>
        </SelectTriggerButtonLabel>
      </SelectTriggerButtonView>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';
SelectTrigger.toString = SelectTriggerButtonView.toString;
